import { useCallback, useMemo, useState } from 'react';

import { Box } from '@mui/system';
import { DialogActions, Divider, Step, StepLabel, Stepper } from '@mui/material';
import { log } from '@utils/logging';
import { CLASSES, GENERAL, SCHEDULING, STRIPE_STEP, WAIVER } from './CreateEventSteps';
import LoadingButton from '@atoms/LoadingButton';
import { SubmitHandler, useFormContext, useFormState } from 'react-hook-form';
import { getDefaultNewEvent, EventForm } from './CreateEventUtils';
import { useStripeAccountRequired } from '@hooks/useStripeAccountRequired';
import { addUpdateEvent } from '@modules/supabase/events';
import { StepIconComponent, StepperConnector } from '@components/molecules/Stepper';
import { STATUS } from '@type/db-types';
import { getMessageFromError } from '@utils/shared-utils';
import useManagedEvents from '@hooks/useManagedEvents';

const steps = ['General', 'Scheduling', 'Divisions & Weight Classes', 'Waiver', 'Payments'];

interface Props {
    step: number;
    onClose: () => void;
    onPrevClick: () => void;
    onNextClick: () => void;
}

export default function NextBackButtonGroup({ step, onClose, onNextClick, onPrevClick }: Props) {
    // initialize stripe account if it doesn't exist already
    const { handleSubmit, getValues, reset } = useFormContext<EventForm>();
    useStripeAccountRequired();
    const [loading, setLoading] = useState(false);
    const { refresh: refreshManagedEvents } = useManagedEvents();
    const onSubmit: SubmitHandler<EventForm> = useCallback(
        (values) => {
            console.log(`CreateEventModal onSubmit`, values);
            setLoading(true);

            // @ts-expect-error stripeDetailsSubmitted is just for validation
            delete values.stripeDetailsSubmitted;

            addUpdateEvent(
                {
                    ...values,
                    // dates need to be in ISO format
                    startAt: new Date(values.startAt).toISOString(),
                    status: STATUS.ACTIVE,
                },
                `event [NextBackButtonGroup]`,
            )
                .then((response) => {
                    if (response?.id) {
                        onClose();
                    } else {
                        console.error('Error adding/adding event');
                    }
                })
                .finally(() => setLoading(false));
        },
        [onClose],
    );

    const { enableNext, errors } = useEnableNext(step);

    const onLastStep = step === STRIPE_STEP;
    const onSecondToLastStep = step === WAIVER;

    // Some props for NextButton change depending on which step you're on
    const nextButtonProps: {
        onClick?: () => void;
        children: string;
    } = useMemo(() => {
        if (onSecondToLastStep) {
            return {
                children: 'Next',
                onClick: () => {
                    // FIXME: build this out
                    // save event to DB with non active status
                    // then update the form with the ID
                    // then call onNextClick
                    const values = getValues();
                    addUpdateEvent(
                        {
                            ...values,
                            // dates need to be in ISO format
                            startAt: new Date(values.startAt).toISOString(),
                            status: STATUS.PENDING,
                        },
                        `event [onSecondToLastStep]`,
                    )
                        .then((response) => {
                            refreshManagedEvents(); // update sidebar if managed events are available
                            if (response?.id) {
                                console.log(
                                    `[onSecondToLastStep] event created, updating form state`,
                                    response,
                                );
                                reset(
                                    getDefaultNewEvent(
                                        response,
                                        values.stripeDetailsSubmitted,
                                        values.createdByUserId,
                                    ),
                                );
                                onNextClick();
                            } else {
                                console.error('Error adding/adding event');
                            }
                        })
                        .finally(() => setLoading(false));
                },
            };
        }
        if (onLastStep) {
            return {
                children: 'Publish',
                onClick: handleSubmit(onSubmit),
                // type: 'submit',
            };
        } else {
            return {
                children: 'Next',
                onClick: onNextClick,
            };
        }
    }, [
        getValues,
        handleSubmit,
        onLastStep,
        onNextClick,
        onSecondToLastStep,
        onSubmit,
        refreshManagedEvents,
        reset,
    ]);

    return (
        <Box>
            <Divider />
            <Box sx={{ paddingBottom: 1, paddingTop: 4, paddingX: 3 }}>
                <Stepper
                    alternativeLabel
                    activeStep={step}
                    connector={<StepperConnector />}
                    sx={{ paddingBottom: 1 }}
                >
                    {steps.length > 1
                        ? steps.map((label) => (
                              <Step key={label}>
                                  <StepLabel StepIconComponent={StepIconComponent}>
                                      {label}
                                  </StepLabel>
                              </Step>
                          ))
                        : null}
                </Stepper>
                <DialogActions>
                    <LoadingButton onClick={onClose}>Cancel</LoadingButton>
                    <Box sx={{ flex: '1 1 auto' }} />
                    {step > 0 ? (
                        <LoadingButton type="button" onClick={onPrevClick}>
                            Prev
                        </LoadingButton>
                    ) : null}

                    <LoadingButton
                        disabled={!enableNext}
                        loading={loading}
                        tooltip={errors}
                        {...nextButtonProps}
                    />
                </DialogActions>
                {/* {onLastStep && Object.keys(errors).length ? (
                    <>
                        {Object.keys(errors).map((errorKey) => (
                            <ErrorText
                                key={errorKey}
                                message={`${errorKey}: ${errors?.[errorKey]?.message}`}
                            />
                        ))}
                    </>
                ) : null} */}
            </Box>
        </Box>
    );
}

const STEP_FIELDS: { [key: number]: (keyof EventForm)[] } = {
    [GENERAL]: ['name', 'description', 'fee'],
    [SCHEDULING]: ['startAt', 'weighInAt', 'location'],
    [CLASSES]: ['divisions', 'weightTolerance'],
    [WAIVER]: ['waiver'],
    [STRIPE_STEP]: ['stripeDetailsSubmitted'],
};

// Exclude fields that are not required to be touched to enable the next button
const TOUCH_EXCLUDED_FIELDS: (keyof EventForm)[] = [
    'weighInAt',
    'weightTolerance',
    'stripeDetailsSubmitted',
];

function useEnableNext(step: number): { enableNext: boolean; errors: string } {
    const { errors, dirtyFields } = useFormState<EventForm>();
    const { getValues } = useFormContext<EventForm>();
    const values = getValues();

    const lastButtonValidation = useCallback(() => {
        if (step === STRIPE_STEP) {
            return values.stripeDetailsSubmitted;
        }
        return true;
    }, [step, values.stripeDetailsSubmitted]);

    const noErrorsForStep = useCallback(
        (fields: (keyof EventForm)[]) =>
            !fields.some((field) => Object.keys(errors).includes(field)),
        [errors],
    );

    const everyFieldTouched = useCallback(
        (fields: (keyof EventForm)[]) =>
            values.id || fields.every((f) => dirtyFields[f] || TOUCH_EXCLUDED_FIELDS.includes(f)),
        [dirtyFields, values.id],
    );

    const stepFields = STEP_FIELDS[step];

    // console.log(`[useEnableNext] errors`, {
    //     noErrorsForStep: noErrorsForStep(stepFields),
    //     everyFieldTouched: everyFieldTouched(stepFields),
    //     lastButtonValidation: lastButtonValidation(),
    //     dirtyFields,
    //     errors,
    //     stepFields,
    //     values,
    // });

    if (!stepFields?.length) {
        log(`useEnableNext unrecognized step: ${step}`);
    }

    return {
        enableNext:
            !!everyFieldTouched(stepFields) &&
            noErrorsForStep(stepFields) &&
            lastButtonValidation(),
        errors: !everyFieldTouched(stepFields)
            ? `Missing required fields: ${stepFields.filter((field) => !dirtyFields[field] && !TOUCH_EXCLUDED_FIELDS.includes(field)).join(', ')}`
            : errors
              ? getMessageFromError(errors)
              : '',
    };
}
